import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, message } = location.state || {};
  const [resendEmail, setResendEmail] = useState(email || '');
  const [resending, setResending] = useState(false);

  const handleResend = async () => {
    const target = (resendEmail || '').trim();
    if (!target) { toast.error('Please enter your email'); return; }
    setResending(true);
    try {
      const res = await axios.post(`${API_URL}/auth/resend-setup-email`, { email: target });
      toast.success(res.data?.message || "If an account exists, we've resent the setup link.");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resend right now. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-500 rounded-full mb-6 animate-bounce">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Payment Successful! 🎉
          </h1>
          <p className="text-xl text-gray-600">
            Welcome to SALESQUALIFYD
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-6">
          {/* Email Check Instructions */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-4">
              <Mail className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  📧 Check Your Email Now
                </h3>
                <p className="text-gray-700 mb-3">
                  We've sent a welcome email to:
                </p>
                <p className="text-xl font-semibold text-blue-600 mb-3">
                  {email || 'your email address'}
                </p>
                <p className="text-gray-700">
                  Click the link in the email to complete your profile setup and create your password.
                </p>
              </div>
            </div>
          </div>

          {/* Spam Warning */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-4">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  ⚠️ Don&apos;t See the Email?
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Check your <strong>spam/junk folder</strong></li>
                  <li>• Look for email from "SALESQUALIFYD Team&quot;</li>
                  <li>• Email subject: "Welcome to SALESQUALIFYD - Complete Your Profile&quot;</li>
                  <li>• Setup link expires in 24 hours</li>
                </ul>
                <div className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="resend-setup-row">
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="your email address"
                    className="flex-1 px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    data-testid="resend-setup-email-input"
                  />
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-red-700 hover:bg-red-800 text-white text-sm font-semibold disabled:opacity-60"
                    data-testid="resend-setup-email-btn"
                  >
                    {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    {resending ? 'Sending…' : 'Resend setup email'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What Happens Next?
            </h3>
            <ol className="space-y-3">
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span className="text-gray-700">Check your email and click the setup link</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span className="text-gray-700">Create your secure password</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span className="text-gray-700">Complete your profile information</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span className="text-gray-700">Start using SALESQUALIFYD immediately!</span>
              </li>
            </ol>
          </div>

          {/* What You Got */}
          <div className="bg-gradient-to-r from-red-50 to-purple-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              ✨ Your Subscription Includes:
            </h3>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
              <div>✅ CRM (Sales Pipeline + Support)</div>
              <div>✅ AI Market Research</div>
              <div>✅ Marketing Automation</div>
              <div>✅ MPO Engine</div>
              <div>✅ All features in your plan</div>
              <div>✅ Cancel any time</div>
            </div>
          </div>
        </div>

        {/* Already have account */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Already completed setup?
          </p>
          <button
            onClick={() => navigate('/login')}
            className="text-red-700 hover:text-red-800 font-semibold"
          >
            Login to your account →
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
